import _ from "lodash";
import { buildSearchQuery } from "./searchQuery";
import { FUND_AMOUNT_FIELDS } from "./approvalFundListFundFields";

export const FUND_SEARCHABLE_FIELDS = [
  "isinCode",
  "fundName",
  "ifastCode",
  "assetClass",
  "fundCurrency",
];

export const FUND_FILTER_COLUMNS = [
  {
    field: "investmentHorizonFilter",
    filterOnly: true,
    filterable: true,
    filter: {
      field: "investmentHorizon",
      label: "Investment Horizon",
      width: 220,
      height: 240,
    },
  },
  {
    field: "riskRatingFilter",
    filterOnly: true,
    filterable: true,
    filter: {
      field: "riskRating",
      label: "Risk Rating",
      width: 200,
      height: 240,
    },
  },
  {
    field: "assetClassFilter",
    filterOnly: true,
    filterable: true,
    filter: {
      field: "assetClass",
      label: "Asset Class",
      width: 220,
      height: 240,
    },
  },
  {
    field: "fundCurrencyFilter",
    filterOnly: true,
    filterable: true,
    filter: {
      field: "fundCurrency",
      label: "Fund Currency",
      width: 200,
      height: 240,
    },
  },
  {
    field: "highYieldBondFundFilter",
    filterOnly: true,
    filterable: true,
    filter: {
      field: "highYieldBondFund",
      label: "High Yield Bond Fund",
      width: 220,
      height: 200,
    },
  },
  {
    field: "complexProductFilter",
    filterOnly: true,
    filterable: true,
    filter: {
      field: "complexProduct",
      label: "Complex Product",
      width: 200,
      height: 200,
    },
  },
  {
    field: "derivativesFundFilter",
    filterOnly: true,
    filterable: true,
    filter: {
      field: "derivativesFund",
      label: "Derivatives Fund",
      tip: "Only Professional Investors or clients with signed Knowledge of Derivatives can select derivative funds. If this client is not eligible, Yes is hidden.\n僅專業投資者或已簽署衍生工具知識聲明的客戶可選擇衍生工具基金。若客戶不符合資格，將隱藏 Yes 選項。",
      width: 200,
      height: 200,
    },
  },
];

const formatFilterLabel = (value) => {
  if (value === true) return "Yes";
  if (value === false) return "No";
  return String(value);
};

export const buildFundDistinctValues = (funds, col) => {
  const field = col.filter?.field;
  if (!field) return [];

  const valueMap = new Map();
  (funds || []).forEach((fund) => {
    const value = fund[field];
    if (value === null || value === undefined || value === "") return;
    if (!valueMap.has(value)) {
      valueMap.set(value, formatFilterLabel(value));
    }
  });

  return Array.from(valueMap.entries())
    .sort(([a], [b]) => String(a).localeCompare(String(b)))
    .map(([value, label]) => ({ value, label }));
};

const matchesSearchQuery = (row, searchQuery) => {
  if (_.isEmpty(searchQuery) || !searchQuery.$or) return true;

  return searchQuery.$or.some((clause) => {
    const field = Object.keys(clause)[0];
    const pattern = clause[field]?.$regex;
    if (!pattern) return false;
    const value = _.get(row, field);
    if (value === null || value === undefined || value === "") return false;
    return new RegExp(pattern, clause[field].$options || "i").test(
      String(value)
    );
  });
};

const matchesTableFilter = (row, tableFilterQuery = {}) => {
  return Object.entries(tableFilterQuery).every(([field, clause]) => {
    if (!clause) return true;

    if (clause.$in) {
      const value = _.get(row, field);
      return clause.$in.some((item) => Object.is(item, value));
    }

    if (clause.$or) {
      return clause.$or.some((part) => {
        const nestedField = Object.keys(part)[0];
        const nestedClause = part[nestedField];
        const value = _.get(row, nestedField);
        return nestedClause?.$in?.some((item) => Object.is(item, value));
      });
    }

    return true;
  });
};

export const filterFunds = (funds, { searchText, tableFilterQuery }) => {
  const searchQuery = buildSearchQuery(searchText, FUND_SEARCHABLE_FIELDS);

  return (funds || []).filter(
    (fund) =>
      matchesSearchQuery(fund, searchQuery) &&
      matchesTableFilter(fund, tableFilterQuery)
  );
};

export const sortFunds = (funds, sort) => {
  if (!sort?.by) return funds;

  const sorted = [...funds];
  sorted.sort((a, b) => {
    const left = _.get(a, sort.by);
    const right = _.get(b, sort.by);

    if (left == null && right == null) return 0;
    if (left == null) return 1;
    if (right == null) return -1;

    if (typeof left === "boolean" || typeof right === "boolean") {
      const comparison = Number(left) - Number(right);
      return sort.order === "desc" ? -comparison : comparison;
    }

    if (
      FUND_AMOUNT_FIELDS.includes(sort.by) ||
      (typeof left === "number" && typeof right === "number")
    ) {
      const comparison = Number(left) - Number(right);
      return sort.order === "desc" ? -comparison : comparison;
    }

    const comparison = String(left).localeCompare(String(right), undefined, {
      numeric: true,
      sensitivity: "base",
    });
    return sort.order === "desc" ? -comparison : comparison;
  });

  return sorted;
};

export const paginateFunds = (funds, { currentPage, pageSize }) => {
  const total = funds.length;
  const totalPage = Math.max(1, Math.ceil(total / pageSize) || 1);
  const safePage = Math.min(Math.max(currentPage, 1), totalPage);
  const start = (safePage - 1) * pageSize;

  return {
    docs: funds.slice(start, start + pageSize),
    total,
    totalPage,
    page: safePage,
  };
};

export const buildTableFilterChange = ({
  setTableFilterQuery,
  setCurrentPage,
}) => {
  return (field, values, possibleValues) => {
    if (!field) return;

    setTableFilterQuery((tableFilterQuery) => {
      const newFilterQuery = { ...tableFilterQuery };

      if (values.length === 0) {
        delete newFilterQuery[field];
        setCurrentPage(1);
        return newFilterQuery;
      }

      if (
        values &&
        possibleValues &&
        values.length === possibleValues.length &&
        newFilterQuery[field]
      ) {
        delete newFilterQuery[field];
        setCurrentPage(1);
        return newFilterQuery;
      }

      newFilterQuery[field] = { $in: values };
      setCurrentPage(1);
      return newFilterQuery;
    });
  };
};
